import React, { useState, useEffect, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import styles from './CarouselSlider.module.css';

interface CarouselSliderProps {
  children: React.ReactNode;
}

function CarouselSlider({ children }: CarouselSliderProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    dragFree: true,
    containScroll: 'trimSnaps',
    slidesToScroll: 1,
    watchResize: true,
    watchDrag: true,
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext]
  );

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      const canPrev = emblaApi.canScrollPrev();
      const canNext = emblaApi.canScrollNext();
      setCanScrollPrev(canPrev);
      setCanScrollNext(canNext);
    };
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('init', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
      emblaApi.off('init', onSelect);
    };
  }, [emblaApi]);

  return (
    <div className={styles.root} onKeyDown={handleKeyDown} tabIndex={0}>
      <div className={styles.container}>
        <div ref={emblaRef} className={styles.viewport}>
          <div className={styles.track}>{children}</div>
        </div>

        {canScrollPrev && (
          <button
            onClick={scrollPrev}
            className={`${styles.arrow} ${styles.previous}`}
          >
            <ChevronLeft size={16} />
          </button>
        )}
        {canScrollNext && (
          <button
            onClick={scrollNext}
            className={`${styles.arrow} ${styles.next}`}
          >
            <ChevronRight size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

export default CarouselSlider;
